import { IntegrationsSyncableState } from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'

import { EmployeePayrollRunNode } from 'modules/compensations/components/EmployeePayrollRunElements'
import { getBadgeGroupedBySyncables } from 'modules/integrations/public'

export type RendererDefinition =
  | { type: 'text'; value: string }
  | { type: 'status'; value: { status: string; label: string } }

export interface ValidationFiltersDataResult {
  aboveLimit?: { totalCount?: number | null } | null
  pendingToDefine?: { totalCount?: number | null } | null
  discrepancy?: { totalCount?: number | null } | null
}

function countAlerts(item: EmployeePayrollRunNode): number {
  const compensationsWithValidations = item.compensations.filter(
    (compensation) => compensation.validations && compensation.validations.length > 0
  )

  return compensationsWithValidations.length
}

function getBadge(item: EmployeePayrollRunNode, syncEnabled: boolean, syncLoading: boolean) {
  if (!syncEnabled) return undefined

  const syncableCompensations = item.compensations.filter(
    (compensation) => compensation.resultType === 'compensation'
  )

  if (!syncableCompensations?.length) return undefined

  const syncableStates: Array<IntegrationsSyncableState | undefined> = syncableCompensations.map(
    (compensation) => compensation.syncableState?.[0]
  )

  return getBadgeGroupedBySyncables(syncLoading, syncableStates)
}

export function createCountItemsWithAlerts(
  validationFiltersDataResult?: ValidationFiltersDataResult | null
) {
  return (validationType: string) => {
    return () => {
      if (!validationFiltersDataResult) return 0

      if (validationType === 'above_limit') {
        return validationFiltersDataResult.aboveLimit?.totalCount ?? 0
      }

      if (validationType === 'pending_to_define') {
        return validationFiltersDataResult.pendingToDefine?.totalCount ?? 0
      }

      if (validationType === 'discrepancy') {
        return validationFiltersDataResult.discrepancy?.totalCount ?? 0
      }

      return 0
    }
  }
}

export function calculateTotalAlerts(
  validationFiltersDataResult?: ValidationFiltersDataResult | null
): number {
  const countItemsWithAlerts = createCountItemsWithAlerts(validationFiltersDataResult)

  return (
    countItemsWithAlerts('above_limit')() +
    countItemsWithAlerts('pending_to_define')() +
    countItemsWithAlerts('discrepancy')()
  )
}

export function renderEmployeeColumn(
  item: EmployeePayrollRunNode,
  syncEnabled: boolean,
  syncLoading: boolean
) {
  const firstName = item.employee?.firstName ?? ''
  const lastName = item.employee?.lastName ?? ''
  const name = [firstName, lastName].filter(Boolean).join(' ')
  return {
    type: 'person' as const,
    value: {
      firstName,
      lastName,
      name,
      deactivated: !item.employee?.active,
      src: buildUrl(item.employee?.avatar?.url ?? '') ?? '',
      badge: getBadge(item, syncEnabled, syncLoading),
    },
  }
}

export function renderAlertsColumn(item: EmployeePayrollRunNode): RendererDefinition {
  const alertCount = countAlerts(item)

  if (alertCount > 0) {
    return {
      type: 'status',
      value: {
        status: 'warning',
        label: String(alertCount),
      },
    }
  }

  return {
    type: 'text',
    value: '-',
  }
}
